import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { GridMenuIcon } from '../components/GridMenuIcon';
import { TravelPlanCard } from '../components/Travel/TravelPlanCard';
import { TravelPlanForm } from '../components/Travel/TravelPlanForm';
import { ConfirmedTravelForm } from '../components/Travel/ConfirmedTravelForm';
import { Plus, Plane, Edit2 } from 'lucide-react';
import { useTravel } from '../hooks/useTravel';
import { useSubscription } from '../contexts/SubscriptionContext';
import { UpgradePrompt } from '../components/Premium/UpgradePrompt';
import type { ConfirmedTravel } from '../types/travel';

export function Travel() {
  const { hasFeature, showUpgradePrompt } = useSubscription();
  const { plans, confirmedTravel, loading, addPlan, addConfirmedTravel, updatePlan, deletePlan } = useTravel();
  const [showAddForm, setShowAddForm] = useState(false);
  const [showConfirmedForm, setShowConfirmedForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState<string | null>(null);
  const [editingTravel, setEditingTravel] = useState<ConfirmedTravel | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const isPremium = hasFeature('travel_organizer');

  if (!isPremium) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto">
          <UpgradePrompt
            feature="Travel Organizer"
            description="Plan and organize your trips with ease. Keep track of itineraries, bookings, and travel documents all in one place."
            onUpgrade={() => showUpgradePrompt('travel_organizer')}
          />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-1">Travel Plans</h1>
            <p className="text-gray-400">Plan and organize your trips</p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading travel plans...</div>
        ) : plans.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            No travel plans yet. Start by adding your first trip!
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
            {plans.map((plan) => (
              <TravelPlanCard
                key={plan.id}
                plan={plan}
                onEdit={() => setEditingPlan(plan.id)}
                onDelete={() => deletePlan(plan.id)}
              />
            ))}
          </div>
        )}

        {confirmedTravel.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-bold mb-4">Confirmed Travel</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
              {confirmedTravel.map((travel) => (
                <div key={travel.id} className="bg-[#141414] rounded-xl p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium">{travel.airline}</h3>
                      <p className="text-sm text-gray-400">Flight {travel.flightNumber}</p>
                    </div>
                    <button
                      onClick={() => {
                        setEditingTravel(travel);
                        setShowConfirmedForm(true);
                      }}
                      className="p-2 hover:bg-black/20 rounded-xl transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <div>
                        <p className="text-gray-400">Departure</p>
                        <p>{travel.departureAirport}</p>
                        <p>{travel.departureTime.toLocaleString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-gray-400">Arrival</p>
                        <p>{travel.arrivalAirport}</p>
                        <p>{travel.arrivalTime.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Add Travel Plan Button */}
        <button
          onClick={() => {
            setEditingPlan(null);
            setShowAddForm(true);
          }}
          className="fixed bottom-24 right-6 md:bottom-8 w-14 h-14 bg-[var(--accent-color)] rounded-full shadow-lg flex items-center justify-center hover:opacity-90 transition-opacity z-50"
        >
          <Plus className="w-6 h-6 text-black" />
        </button>

        {/* Add Travel Button */}
        <button
          onClick={() => {
            setEditingTravel(null);
            setShowConfirmedForm(true);
          }}
          className="fixed bottom-24 right-24 md:bottom-8 w-14 h-14 bg-[#bbdde5] rounded-full shadow-lg flex items-center justify-center hover:opacity-90 transition-opacity z-50"
        >
          <Plane className="w-6 h-6 text-black" />
        </button>

        {(showAddForm || editingPlan) && (
          <TravelPlanForm
            plan={editingPlan ? plans.find(p => p.id === editingPlan) : undefined}
            onSubmit={async (planData) => {
              if (editingPlan) {
                await updatePlan(editingPlan, planData);
              } else {
                await addPlan(planData);
              }
              setShowAddForm(false);
              setEditingPlan(null);
            }}
            onClose={() => {
              setShowAddForm(false);
              setEditingPlan(null);
            }}
          />
        )}
        
        {(showConfirmedForm || editingTravel) && (
          <ConfirmedTravelForm
            initialTravel={editingTravel || undefined}
            onSubmit={async (data) => {
              try {
                await addConfirmedTravel(data);
                setShowConfirmedForm(false);
                setEditingTravel(null);
              } catch (err) {
                console.error('Error adding confirmed travel:', err);
                alert('Failed to add confirmed travel. Please try again.');
              }
            }}
            onClose={() => {
              setShowConfirmedForm(false);
              setEditingTravel(null);
            }}
          />
        )}
      </div>
    </Layout>
  );
}